/**
 * features/finance/CashUp.tsx
 * ------------------------------------------------------------------
 * FINANCE — Cash Up (daily takings reconciliation), read-only report.
 *
 * Lists every receipt (payment) received in a chosen period — by day,
 * week or month — broken down by payment method, for end-of-day /
 * end-of-period reconciliation ("cash up" / Z-report).
 *
 * Accounting basis:
 *  • Reconciled by VALUE DATE (payment.receivedAt), not entry date.
 *    Receipts captured on a later calendar day than they were received
 *    are flagged "late" so a closed period's variance can be explained.
 *  • Grouped by method. Cash is physically banked (the true cash-up);
 *    EFT/card/mobile already settle to the bank, so an
 *    "exclude bank-posted" toggle lets you see pure over-the-counter
 *    takings (payments posted via bank reconciliation carry a
 *    statementLineId and are treated as already-in-bank).
 *
 * Two parts:
 *  • REPORT — receipts by method for the period, with CSV export.
 *  • CLOSE  — a finance manager enters the cash actually counted/
 *    banked; the variance is recorded and the period is locked
 *    into a financeReconciliations record (audited, reopenable with a
 *    reason). EFT/card/mobile are shown read-only (they settle to the
 *    bank and reconcile on the bank side). Receipts posted into a
 *    period after it was closed are surfaced as "posted after close".
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { Calculator, ChevronLeft, ChevronRight, Download, Printer, Landmark, Lock, Unlock, AlertTriangle, FileText } from 'lucide-react';
import { AccentButton, GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { NumberField } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { buildCashUpPdf } from '@/lib/cashUpPdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  fmtPula, round2, PAYMENT_METHOD_LABELS, reconciliationId,
  type PaymentMethod, type Payment, type ReconcileMethodTotals, type FinanceReconciliation,
} from '@/lib/financeCore';

type Mode = 'day' | 'week' | 'month';
const MODES: { id: Mode; label: string }[] = [
  { id: 'day', label: 'Day' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' },
];
// Cash first — the order an accounts office cashes up in.
const METHOD_ORDER: PaymentMethod[] = ['cash', 'eft', 'card', 'mobile', 'other'];

/* ----- date helpers (local time; Monday-based weeks) ----- */
function atMidnight(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOf(mode: Mode, anchor: Date): Date {
  const d = atMidnight(anchor);
  if (mode === 'day') return d;
  if (mode === 'week') { const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return d; }
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOf(mode: Mode, anchor: Date): Date {
  const s = startOf(mode, anchor); const e = new Date(s);
  if (mode === 'day') e.setDate(e.getDate() + 1);
  else if (mode === 'week') e.setDate(e.getDate() + 7);
  else e.setMonth(e.getMonth() + 1);
  return e;
}
function shiftAnchor(mode: Mode, anchor: Date, dir: number): Date {
  const d = new Date(anchor);
  if (mode === 'day') d.setDate(d.getDate() + dir);
  else if (mode === 'week') d.setDate(d.getDate() + 7 * dir);
  else d.setMonth(d.getMonth() + dir);
  return d;
}
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function localDateKey(iso: string): string { return ymd(new Date(iso)); }
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function periodLabel(mode: Mode, anchor: Date): string {
  const s = startOf(mode, anchor);
  if (mode === 'day') return fmtDay(s);
  if (mode === 'month') return s.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const e = new Date(endOf(mode, anchor)); e.setDate(e.getDate() - 1);
  const sameMonth = s.getMonth() === e.getMonth();
  const sd = s.toLocaleDateString(undefined, { day: 'numeric', month: sameMonth ? undefined : 'short' });
  const ed = e.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${sd} – ${ed}`;
}
function periodKey(mode: Mode, anchor: Date): string {
  const s = startOf(mode, anchor);
  if (mode === 'month') return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`;
  return ymd(s);
}

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function CashUp() {
  const data = useFinanceData();
  const [mode, setMode] = useState<Mode>('day');
  const [anchorStr, setAnchorStr] = useState<string>(ymd(new Date()));
  const [methodFilter, setMethodFilter] = useState<'all' | PaymentMethod>('all');
  const [cashierFilter, setCashierFilter] = useState<string>('all');
  const [excludeBank, setExcludeBank] = useState(false);

  const anchor = useMemo(() => new Date(`${anchorStr}T12:00:00`), [anchorStr]);
  const start = useMemo(() => startOf(mode, anchor), [mode, anchor]);
  const end = useMemo(() => endOf(mode, anchor), [mode, anchor]);

  // Receipts in the period, by value date.
  const inPeriod = useMemo(() => {
    return data.payments.filter((p) => {
      if (!p.receivedAt) return false;
      const t = new Date(p.receivedAt);
      return t >= start && t < end;
    });
  }, [data.payments, start, end]);

  const cashiers = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of inPeriod) if (p.recordedBy) m.set(p.recordedBy, p.recordedByName ?? p.recordedBy);
    return Array.from(m, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [inPeriod]);

  // Scope = period minus bank-posted (optional) and cashier filter — drives breakdown + totals.
  const scoped = useMemo(() => {
    return inPeriod.filter((p) => {
      if (excludeBank && p.statementLineId) return false;
      if (cashierFilter !== 'all' && p.recordedBy !== cashierFilter) return false;
      return true;
    });
  }, [inPeriod, excludeBank, cashierFilter]);

  // The visible list additionally honours the method filter.
  const rows = useMemo(() => {
    const list = scoped.filter((p) => methodFilter === 'all' || p.method === methodFilter);
    return [...list].sort((a, b) => (a.receivedAt ?? '').localeCompare(b.receivedAt ?? ''));
  }, [scoped, methodFilter]);

  const byMethod = useMemo(() => {
    const m = new Map<PaymentMethod, { count: number; amount: number }>();
    for (const p of scoped) {
      const cur = m.get(p.method) ?? { count: 0, amount: 0 };
      cur.count += 1; cur.amount = round2(cur.amount + (p.amount || 0));
      m.set(p.method, cur);
    }
    return m;
  }, [scoped]);

  const grand = useMemo(() => round2(scoped.reduce((a, p) => a + (p.amount || 0), 0)), [scoped]);
  const lateCount = useMemo(
    () => scoped.filter((p) => p.createdAt && p.receivedAt && localDateKey(p.createdAt) > localDateKey(p.receivedAt)).length,
    [scoped],
  );
  const isLate = (p: Payment) => !!(p.createdAt && p.receivedAt && localDateKey(p.createdAt) > localDateKey(p.receivedAt));

  function exportCsv() {
    const header = ['Date', 'Time', 'Receipt No', 'Student', 'Method', 'Reference', 'Cashier', 'Amount', 'Bank-posted', 'Late capture'];
    const lines = rows.map((p) => [
      localDateKey(p.receivedAt), fmtTime(p.receivedAt), p.receiptNo ?? '', p.studentName ?? '',
      PAYMENT_METHOD_LABELS[p.method], p.reference ?? '', p.recordedByName ?? p.recordedBy ?? '',
      round2(p.amount || 0).toFixed(2), p.statementLineId ? 'yes' : '', isLate(p) ? 'yes' : '',
    ].map(csvCell).join(','));
    const totalRow = ['', '', '', '', '', '', 'TOTAL', '', grand.toFixed(2), '', ''].map(csvCell).join(',');
    const csv = [header.map(csvCell).join(','), ...lines, totalRow].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cashup_${mode}_${periodKey(mode, anchor)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  }

  // ---- Cash-up close -------------------------------------------------
  const { userData, permissions } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? undefined };
  const canClose = !!permissions?.canManageFinanceConfig;

  const pKey = periodKey(mode, anchor);
  const recId = reconciliationId(mode, pKey);
  const existing = useMemo(
    () => data.reconciliations.find((r) => r.id === recId) ?? null,
    [data.reconciliations, recId],
  );
  const isClosed = existing?.status === 'reconciled';

  // Official totals for the WHOLE period (ignore view filters) — the record of truth.
  const official = useMemo<ReconcileMethodTotals>(() => {
    const t: ReconcileMethodTotals = { cash: 0, eft: 0, card: 0, mobile: 0, other: 0 };
    for (const p of inPeriod) t[p.method] = round2(t[p.method] + (p.amount || 0));
    return t;
  }, [inPeriod]);
  const officialGrand = useMemo(() => round2(inPeriod.reduce((a, p) => a + (p.amount || 0), 0)), [inPeriod]);

  const [countedCash, setCountedCash] = useState(0);
  const [bankingRef, setBankingRef] = useState('');
  const [note, setNote] = useState('');
  const [varianceReason, setVarianceReason] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  // Prefill inputs from an existing record (so a reopened period shows prior values), else reset.
  useEffect(() => {
    if (existing) {
      setCountedCash(existing.counted.cash);
      setBankingRef(existing.bankingRef ?? '');
      setNote(existing.note ?? '');
      setVarianceReason(existing.varianceReason ?? '');
    } else {
      setCountedCash(0); setBankingRef(''); setNote(''); setVarianceReason('');
    }
    setShowReopen(false); setReopenReason('');
  }, [recId, existing]);

  const varCash = round2(countedCash - official.cash);
  const hasVariance = varCash !== 0;

  // Receipts posted into this period AFTER it was closed (books changed post-close).
  const postedAfterClose = useMemo(() => {
    if (!isClosed || !existing) return [];
    return inPeriod.filter((p) => p.createdAt && p.createdAt > existing.reconciledAt);
  }, [isClosed, existing, inPeriod]);

  async function closePeriod() {
    if (!canClose || busy) return;
    if (hasVariance && !varianceReason.trim()) {
      setSc({ show: true, status: 'error', message: 'Enter a reason for the variance before closing.' });
      return;
    }
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Closing period…' });
    try {
      const rec: Omit<FinanceReconciliation, 'createdAt' | 'updatedAt'> = {
        id: recId, mode, periodKey: pKey,
        periodStart: start.toISOString(), periodEnd: end.toISOString(),
        systemTotals: official, receiptCount: inPeriod.length, grandTotal: officialGrand,
        counted: { cash: round2(countedCash) },
        variance: { cash: varCash },
        bankingRef: bankingRef.trim() || undefined,
        note: note.trim() || undefined,
        varianceReason: hasVariance ? varianceReason.trim() : undefined,
        status: 'reconciled',
        reconciledBy: actor.id, reconciledByEmail: actor.email, reconciledAt: new Date().toISOString(),
      };
      await finance.reconciliations.set(recId, rec as unknown as Record<string, unknown>);
      await recordFinanceAudit({
        action: 'cashup.close', actor, subjectId: recId,
        after: { grandTotal: officialGrand, varCash, receiptCount: inPeriod.length },
      });
      setSc({ show: true, status: 'success', message: `${periodLabel(mode, anchor)} closed.` });
    } catch (e) {
      setSc({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function reopenPeriod() {
    if (!canClose || busy || !existing) return;
    if (!reopenReason.trim()) {
      setSc({ show: true, status: 'error', message: 'Enter a reason to reopen this period.' });
      return;
    }
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Reopening…' });
    try {
      await finance.reconciliations.update(recId, {
        status: 'reopened',
        reopenedBy: actor.id, reopenedByEmail: actor.email,
        reopenedAt: new Date().toISOString(), reopenReason: reopenReason.trim(),
      });
      await recordFinanceAudit({ action: 'cashup.reopen', actor, subjectId: recId, after: { reason: reopenReason.trim() } });
      setSc({ show: true, status: 'success', message: 'Period reopened — you can re-close it.' });
      setShowReopen(false);
    } catch (e) {
      setSc({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function downloadSheet() {
    if (!existing) return;
    try {
      const logo = await loadLogoDataUrl();
      buildCashUpPdf(existing, {
        logoDataUrl: logo,
        periodLabel: periodLabel(mode, anchor),
        receipts: inPeriod,
      }).save(`cashup_${mode}_${pKey}.pdf`);
    } catch (e) {
      setSc({ show: true, status: 'error', message: (e as Error).message });
    }
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading finance…" /></div>;
  }

  return (
    <div className="space-y-6">
      <GradientHero icon={Calculator} title="Cash Up" subtitle="Daily takings reconciliation — receipts by method" />

      <div className="space-y-5">
      {/* period controls */}
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    mode === m.id ? 'seg-on' : 'seg-off'
                  }`}
                >{m.label}</button>
              ))}
            </div>
            <button onClick={() => setAnchorStr(ymd(shiftAnchor(mode, anchor, -1)))} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" aria-label="Previous"><ChevronLeft size={18} /></button>
            <input type="date" value={anchorStr} onChange={(e) => setAnchorStr(e.target.value)} className={glassFieldClass('py-1.5 text-sm')} />
            <button onClick={() => setAnchorStr(ymd(shiftAnchor(mode, anchor, 1)))} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" aria-label="Next"><ChevronRight size={18} /></button>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 select-none">
            <input type="checkbox" checked={excludeBank} onChange={(e) => setExcludeBank(e.target.checked)} className="rounded" />
            Over-the-counter only <span className="text-gray-400">(exclude bank-posted)</span>
          </label>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Calculator size={16} className="text-[var(--accent-link,#0d9488)]" />
          <span className="font-semibold text-gray-800 dark:text-gray-100">{periodLabel(mode, anchor)}</span>
          <span className="text-sm text-gray-400">· {scoped.length} receipt{scoped.length === 1 ? '' : 's'}</span>
        </div>
      </SoftCard>

      {/* headline */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip tone="emerald" value={fmtPula(grand)} label="Total received" />
        <StatChip tone="blue" value={String(scoped.length)} label="Receipts" />
        <StatChip tone="slate" value={String(byMethod.size)} label="Methods used" />
        <StatChip tone={lateCount ? 'gold' : 'slate'} value={String(lateCount)} label="Captured late" />
      </div>

      {/* method breakdown */}
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">By payment method</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {METHOD_ORDER.map((m) => {
            const v = byMethod.get(m) ?? { count: 0, amount: 0 };
            const muted = v.count === 0;
            return (
              <div key={m} className={`rounded-xl border p-3 ${muted ? 'border-gray-100 dark:border-gray-800 opacity-60' : 'border-gray-200 dark:border-gray-700'}`}>
                <div className="text-xs text-gray-400">{PAYMENT_METHOD_LABELS[m]}</div>
                <div className="font-semibold text-gray-800 dark:text-gray-100 mt-1">{fmtPula(v.amount)}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{v.count} receipt{v.count === 1 ? '' : 's'}</div>
              </div>
            );
          })}
        </div>
      </SoftCard>

      {/* list + filters */}
      <SoftCard pad="p-0" hover={false}>
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-gray-100 dark:border-gray-700">
          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value as 'all' | PaymentMethod)} className={glassFieldClass('py-1.5 text-sm')}>
            <option value="all">All methods</option>
            {METHOD_ORDER.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
          </select>
          {cashiers.length > 1 && (
            <select value={cashierFilter} onChange={(e) => setCashierFilter(e.target.value)} className={glassFieldClass('py-1.5 text-sm')}>
              <option value="all">All cashiers</option>
              {cashiers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportCsv} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 disabled:opacity-50"><Download size={14} /> CSV</button>
            <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"><Printer size={14} /> Print</button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">No receipts in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pl-4 pr-3 font-medium">Time</th>
                  <th className="py-2 px-3 font-medium">Receipt</th>
                  <th className="py-2 px-3 font-medium">Student</th>
                  <th className="py-2 px-3 font-medium">Method</th>
                  <th className="py-2 px-3 font-medium">Reference</th>
                  <th className="py-2 px-3 font-medium">Cashier</th>
                  <th className="py-2 pr-4 pl-3 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-gray-50 dark:border-gray-800/60">
                    <td className="py-2 pl-4 pr-3 text-gray-500 whitespace-nowrap">
                      {fmtTime(p.receivedAt)}
                      {isLate(p) && <span className="ml-1.5 text-[10px] font-medium text-amber-600 dark:text-amber-300" title={`Received ${localDateKey(p.receivedAt)}, captured ${localDateKey(p.createdAt)}`}>late</span>}
                    </td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">{p.receiptNo ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{p.studentName ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {PAYMENT_METHOD_LABELS[p.method]}
                      {p.statementLineId && <span title="Posted via bank reconciliation (already in bank)"><Landmark size={12} className="inline ml-1 text-gray-400" /></span>}
                    </td>
                    <td className="py-2 px-3 text-gray-500">{p.reference ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{p.recordedByName ?? p.recordedBy ?? '—'}</td>
                    <td className="py-2 pr-4 pl-3 text-right font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{fmtPula(p.amount || 0)}</td>
                  </tr>
                ))}
                <tr className="font-semibold text-gray-800 dark:text-gray-100">
                  <td className="py-2.5 pl-4 pr-3" colSpan={6}>Total · {periodLabel(mode, anchor)}</td>
                  <td className="py-2.5 pr-4 pl-3 text-right">{fmtPula(grand)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>

      {/* ---- Cash-up close ---- */}
      <SoftCard hover={false}>
        <div className="flex items-center gap-2 mb-3">
          {isClosed ? <Lock size={16} className="text-emerald-600" /> : <Unlock size={16} className="text-gray-400" />}
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
            Cash up · {periodLabel(mode, anchor)}
          </h3>
          {isClosed && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Closed</span>}
          {existing?.status === 'reopened' && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Reopened</span>}
        </div>

        {/* expected, all methods */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          {METHOD_ORDER.map((m) => (
            <div key={m} className="rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
              <div className="text-[11px] text-gray-400">{PAYMENT_METHOD_LABELS[m]}</div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{fmtPula(official[m])}</div>
            </div>
          ))}
        </div>

        {isClosed && existing ? (
          /* locked summary */
          <div className="space-y-3 text-sm">
            <div className="grid sm:grid-cols-2 gap-3">
              <ReconLine label="Cash — expected" value={fmtPula(existing.systemTotals.cash)} />
              <ReconLine label="Cash — counted" value={fmtPula(existing.counted.cash)} />
            </div>
            <div className={`rounded-lg px-3 py-2 ${existing.variance.cash === 0 ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'}`}>
              Cash variance {fmtPula(existing.variance.cash)}
              {existing.varianceReason && <span className="block text-xs mt-1 opacity-90">Reason: {existing.varianceReason}</span>}
            </div>
            {existing.bankingRef && <div className="text-gray-500">Banking ref: <span className="text-gray-800 dark:text-gray-200">{existing.bankingRef}</span></div>}
            {existing.note && <div className="text-gray-500">Note: <span className="text-gray-800 dark:text-gray-200">{existing.note}</span></div>}
            <div className="text-xs text-gray-400">
              Closed by {existing.reconciledByEmail ?? existing.reconciledBy} · {new Date(existing.reconciledAt).toLocaleString()}
            </div>

            {postedAfterClose.length > 0 && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-amber-700 dark:text-amber-300 text-xs flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{postedAfterClose.length} receipt(s) were posted into this period after it was closed. Reopen and re-close to bring the cash-up back into balance.</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button onClick={downloadSheet} className="flex items-center gap-1.5 text-sm font-medium text-[var(--accent-link,#0d9488)] hover:underline"><FileText size={14} /> Cash-up sheet (PDF)</button>
              {canClose && (
                showReopen ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs text-gray-500 mb-1">Reason for reopening</label>
                      <input value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} className={glassFieldClass('py-2 text-sm')} placeholder="e.g. correcting a miskeyed receipt" />
                    </div>
                    <button onClick={reopenPeriod} disabled={busy} className="px-3 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">Confirm reopen</button>
                    <button onClick={() => setShowReopen(false)} className="px-3 py-2 text-sm text-gray-500">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowReopen(true)} className="flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700 font-medium"><Unlock size={14} /> Reopen period</button>
                )
              )}
            </div>
          </div>
        ) : (
          /* entry form */
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <CountField label="Cash counted / banked" expected={official.cash} value={countedCash} onValue={setCountedCash} variance={varCash} disabled={!canClose} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Banking reference (deposit slip no.)</label>
                <input value={bankingRef} onChange={(e) => setBankingRef(e.target.value)} disabled={!canClose} className={glassFieldClass('py-2 text-sm')} placeholder="optional" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} disabled={!canClose} className={glassFieldClass('py-2 text-sm')} placeholder="optional" />
              </div>
            </div>
            {hasVariance && (
              <div>
                <label className="block text-xs text-amber-600 dark:text-amber-300 mb-1">Variance reason (required — doesn't balance)</label>
                <input value={varianceReason} onChange={(e) => setVarianceReason(e.target.value)} disabled={!canClose} className={glassFieldClass('py-2 text-sm')} placeholder="e.g. P50 till short, noted and signed" />
              </div>
            )}
            {canClose ? (
              <AccentButton icon={Lock} onClick={closePeriod} disabled={busy || inPeriod.length === 0}>
                {busy ? 'Closing…' : 'Close & lock period'}
              </AccentButton>
            ) : (
              <p className="text-xs text-gray-400">Closing a period is restricted to finance managers. You can view and export the report.</p>
            )}
          </div>
        )}
      </SoftCard>

      <p className="text-xs text-gray-400">
        Reconciled by value date (when the payment was received). Receipts captured on a later day are tagged
        “late”. Closing records cash counted against the system total; a noted variance is allowed,
        and a closed period can be reopened (with a reason) — every close and reopen is audited.
      </p>

      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} autoDismissMs={3500} />
      </div>
    </div>
  );
}

function ReconLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800 dark:text-gray-100">{value}</span>
    </div>
  );
}

function CountField(props: {
  label: string; expected: number; value: number;
  onValue: (n: number) => void; variance: number; disabled: boolean;
}) {
  const { label, expected, value, onValue, variance, disabled } = props;
  const tone = variance === 0
    ? 'text-emerald-600 dark:text-emerald-300'
    : 'text-amber-600 dark:text-amber-300';
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <NumberField value={value} onValue={onValue} disabled={disabled} className={glassFieldClass('py-2')} />
      <div className="mt-1 flex justify-between text-[11px]">
        <span className="text-gray-400">Expected {fmtPula(expected)}</span>
        <span className={tone}>Variance {fmtPula(variance)}</span>
      </div>
    </div>
  );
}
